import React from "react";
import { observable, action, computed } from "mobx";
import toastService from "./ToastService";
import { AutoValidationErrorNotificationMessage } from "../general/AutoValidationErrorNotificationMessage";
import HttpUtils from "src/utils/HttpUtils";
import validationService from "./ValidationService";

const downloadjs = require("downloadjs");

const RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE =
  "Result size exceeds limit. Please use filters.";

export default class HttpServices {
  @observable str_URL_BASE;

  constructor() {
    this.str_URL_BASE = HttpUtils.getBaseUrl();
  }

  // generic request for fetching entities based on params
  @action
  fetchEntities = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Entities";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const response = await window.fetch(this.str_URL_BASE + "query", {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(params),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      return response.json();
    } catch (error) {
      console.error("service_query_all_ecb_user error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  sendQueryWizardAccessRequest = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Entities";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const response = await window.fetch(
        this.str_URL_BASE + "statistics/queryWizard",
        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(params),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);
      // return response.json();
    } catch (error) {
      console.error("service_query_all_ecb_user error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchScheduledJobs = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query All Scheduled Promotion Jobs";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "promotionSheduler";
      const response = await window.fetch(fullUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch all the entities: ", error);
      return [];
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  updateScheduledJobs = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Remove Scheduled Promotion Job by Id";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const response = await window.fetch(
        this.str_URL_BASE + "promotionSheduler",
        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(params),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);
      return response.json();
    } catch (error) {
      console.error("Scheduler Promotion Job error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchAccessUsers = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Users at login";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const response = await window.fetch(this.str_URL_BASE + "query", {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(params),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      return response.json();
    } catch (error) {
      console.error("service_query_all_ecb_user error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_filteredEntities = (ob_prefilteredEntities, cb) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query Prefiltered Entities";
    HttpUtils.addRequest(requestId, requestDescription);
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const ob_POST_Config = {
      method: "POST",
      credentials: "same-origin",
      headers: HttpUtils.amendHeadersIfNeeded(headers),
      redirect: "manual",
      body: JSON.stringify(ob_prefilteredEntities),
    };

    window
      .fetch(this.str_URL_BASE + "query", ob_POST_Config)
      .then((response) => {
        HttpUtils.reloadIfRedirectedToLogin(response);
        if (response.ok && response.status === 200) {
          return response.json();
        } else if (response.status === 418) {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
          return response.json();
        }
      })
      .then((res) => {
        HttpUtils.removeRequest(requestId);
        return cb(res);
      })
      .catch((err) => {
        console.log(err);
        HttpUtils.removeRequest(requestId);
      });
  };

  @action
  service_fetch_ma = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Maintenance Agencies";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const STR_DATA_END_POINT = this.str_URL_BASE + "query/";

      const queryJSON = {
        type: "MaintenanceAgency",
        filter: {
          type: "MaintenanceAgency",
        },
      };
      const response = await window.fetch(
        STR_DATA_END_POINT,

        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(queryJSON),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      const responseJson = await response.json();

      return responseJson;
    } catch (error) {
      console.log("service_fetch_ma err http", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_frameworks = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Frameworks";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const STR_DATA_END_POINT = this.str_URL_BASE + "query/";

      const queryJSON = {
        type: "Framework",
        filter: {
          type: "Framework",
        },
      };
      const response = await window.fetch(
        STR_DATA_END_POINT,

        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(queryJSON),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.log("service_fetch_framework err http", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_frameworks_with_cube_type_counts = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Frameworks With Cube Type Counts";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const STR_DATA_END_POINT = this.str_URL_BASE + "queryfw/";

      const response = await window.fetch(
        STR_DATA_END_POINT,

        {
          method: "GET",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);
      return await response.json();
    } catch (error) {
      console.log("service_fetch_frameworks_with_cube_types err http", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_ALLMA = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Maintenance Agency Owner Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const STR_DATA_END_POINT = this.str_URL_BASE + "querymao/";

      const response = await window.fetch(
        STR_DATA_END_POINT,

        {
          method: "GET",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.log("service_fetch_ALLMA err http", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_po_data = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Project Owner Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const STR_DATA_END_POINT = this.str_URL_BASE + "querypo/";

      const response = await window.fetch(
        STR_DATA_END_POINT,

        {
          method: "GET",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.log("service_fetch_ALLMA err http", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_query_all_Frameworks = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Frameworks";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const queryJSON = {
        type: "Framework",
        filter: {
          type: "Framework",
        },
      };

      const str_urlBase = this.str_URL_BASE + "query/";

      const response = await window.fetch(
        str_urlBase,

        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(queryJSON),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.error("service_query_all_Frameworks error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_query_all_Maintenance_Agencies = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Maintenance Agencies";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const queryJSON = {
        type: "MaintenanceAgency",
        filter: {
          type: "MaintenanceAgency",
        },
      };

      const str_urlBase = this.str_URL_BASE + "query/";

      const response = await window.fetch(
        str_urlBase,

        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(queryJSON),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.error("service_query_all_Maintenance_Agencies error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_query_all_ecb_user = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query All Users";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const jsonToSend = {
        type: "Actor",
        filter: { type: "Actor" },
      };

      const str_endPoint_address_all_ecb_users = this.str_URL_BASE + "query/";

      const response = await window.fetch(
        str_endPoint_address_all_ecb_users,

        {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(jsonToSend),
        }
      );
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
      console.error("service_query_all_ecb_user error", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  service_fetch_all_entities = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query All Entity Types";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "excel/info";
      const response = await window.fetch(fullUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch all the entities: ", error);
      return [];
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  h_service_fetch_user_data = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query User Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const urlBase = this.str_URL_BASE + "queryuser/";

      const response = await window.fetch(urlBase, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      return await response.json();
    } catch (error) {
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetch_dictionary_administrator_data = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Dictionary Administrator Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "queryda";
      const response = await window.fetch(fullUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch Dictionary Administrator data: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetch_system_administrator_data = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query System Administrator Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "querysa";
      const response = await window.fetch(fullUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch System Administrator data: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchApprovalsChangesData = async (fetchParams) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Changes/Approvals Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const {
        approvalStatusesAndBranchTypes,
        objectTypes,
        includeChanges,
        limit,
        action,
        trackingParams,
        entityFilter
      } = fetchParams;

      const fullUrl = this.str_URL_BASE + "querychanges";
      const response = await window.fetch(fullUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify({
          entityTypes: objectTypes,
          entityFilter: entityFilter,
          comparisonsAgainstBranchTypes: includeChanges
            ? approvalStatusesAndBranchTypes.branchTypes
            : [],
          limit: limit,
          action: action,
          trackingParams: trackingParams,
        }),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      if (response.status === 500) {
        const promise = response.json();
        Promise.resolve(promise).then((value) => {
          if (value.validationRequestId) {
            toastService.notifyWithComponent(
              <AutoValidationErrorNotificationMessage
                message={value.resultMessage}
                onClick={() =>
                  validationService.exportValidationResults(
                    value.validationRequestId,
                    "xlsx"
                  )
                }
              />,
              "error"
            );
          } else {
            const message = value.resultMessage;
            toastService.func_notify(message, "error");
          }
        });
        return null;
      }
      let json = await response.json();
      if (json.resultMessage) {
        toastService.func_notify(json.resultMessage, "info");
      }
      return json;
    } catch (error) {
      console.log("Failed to fetch approvals-changes data: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchActionsDataFromUser = async (queryParams) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Actions/Events Data from specific user";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "query/filterbyaid";
      const response = await window.fetch(fullUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify({
          type: queryParams.filter.type,
          filter: queryParams.filter,
          limit: queryParams.limit,
        }),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch Action from specific User: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchActionsEventsData = async (queryParams) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Query Actions/Events Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const fullUrl = this.str_URL_BASE + "query";
      const response = await window.fetch(fullUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify({
          type: queryParams.filter.type,
          filter: queryParams.filter,
          limit: queryParams.limit,
        }),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch Event Log data: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetch_entityOrOrchestration_data = async (entityOrOrchestration) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription =
        "Query System Administrator Entity/Orchestration Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const jsonToSend = {
        type: entityOrOrchestration,
        filter: { type: entityOrOrchestration },
      };

      const fullUrl = this.str_URL_BASE + "query";
      const response = await window.fetch(fullUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(jsonToSend),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      if (response.status === 418) {
        const promise = response.text();
        Promise.resolve(promise).then((value) => {
          toastService.func_notify(
            RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
            "error"
          );
        });
        return null;
      }
      return await response.json();
    } catch (error) {
      console.log("Failed to fetch Entity/Orchestration data: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchContentManagementTableData = async (fetchParams) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription =
        "Query Content Management " + fetchParams.tableName + " Table Data";
      if (fetchParams.hidden !== true) {
        HttpUtils.addRequest(requestId, requestDescription);
      }
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });

      const url = this.str_URL_BASE + "query";
      const response = await window.fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify({
          type: fetchParams.tableName,
          filter: fetchParams.filter,
          limit: {
            limit: fetchParams.pageSize,
            offset: fetchParams.queryOffset,
          },
        }),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      const responseJson = await response.json();
      return responseJson;
    } catch (error) {
      console.log("CM table data fetch failed: ", error);
    } finally {
      if (fetchParams.hidden !== true) {
        HttpUtils.removeRequest(requestId);
      }
    }
  };

  @action
  func_sendGeneralMessage = async (action, callback) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Action: " + action.actionType;
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      await window
        .fetch(this.str_URL_BASE + "action", {
          method: "POST",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
          body: JSON.stringify(action),
        })
        .then((response) => {
          HttpUtils.reloadIfRedirectedToLogin(response);
          if (response.ok) {
            const promise = response.json();
            Promise.resolve(promise).then((value) => {
              toastService.func_notifyIfNeededAccordingResponseEvent(
                value
              );
            });
            return promise;
          } else if (response.status === 418) {
            const promise = response.text();
            Promise.resolve(promise).then((value) => {
              toastService.func_notify(
                RESULT_SIZE_EXCEEDS_LIMIT_MESSAGE,
                "error"
              );
            });
            return null;
          } else if (response.status === 500) {
            const promise = response.json();
            Promise.resolve(promise).then((value) => {
              if (value.validationRequestId) {
                toastService.notifyWithComponent(
                  <AutoValidationErrorNotificationMessage
                    message={value.resultMessage}
                    onClick={() =>
                      validationService.exportValidationResults(
                        value.validationRequestId,
                        "xlsx"
                      )
                    }
                  />,
                  "error"
                );
              } else {
                toastService.func_notify(
                  value.resultMessage
                    ? value.resultMessage
                    : "Unexpected error occurred",
                  "error"
                );
              }
            });
            return null;
          }
        })
        .then((res) => {
          if (callback) {
            callback(res);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.log(error.message);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  sendExportRequest = async (exportUrl, mimeType, fileName) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Export Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      await window
        .fetch(exportUrl, {
          method: "GET",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        })
        .then(function (response) {
          if (response.status === 500) {
            const promise = response.json();
            Promise.resolve(promise).then((value) => {
              toastService.func_notify(
                value?.message ? value.message : "Error while exporting",
                "error"
              );
              return null;
            });
          }
          return response.blob();
        })
        .then(function (excelBlob) {
          if (excelBlob !== null) {
            downloadjs(excelBlob, fileName, mimeType);
          }
        });
    } catch (error) {
      console.log("Export request failed: ", error);
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  sendImportRequest = async (importUrl, formData) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Import Data";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const response = await window.fetch(importUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: formData,
      });
      HttpUtils.reloadIfRedirectedToLogin(response);
      return await response.json();
    } catch (error) {
      toastService.func_notify(error?.message, "error");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  generateProductionChangelog = async (frameworkId) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Generate Production Changelog";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const frameworkIdParam = frameworkId ? "?frameworkId=" + frameworkId : "";
      const url = this.str_URL_BASE + "history/generate" + frameworkIdParam;
      await window
        .fetch(url, {
          method: "GET",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        })
        .then((response) => {
          HttpUtils.reloadIfRedirectedToLogin(response);
          if (response.status === 200) {
            toastService.func_notify(
              "Production Changelog generated successfully",
              "info"
            );
          } else {
            toastService.func_notify(
              "Production Changelog generation failed",
              "error"
            );
          }
        });
    } catch (error) {
      console.error(
        "Manual triggering of the Production Changelog generation failed"
      );
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchSuggestionFields = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Fetch suggestion fields";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const url = this.str_URL_BASE + "suggest/fields";

      const response = await window.fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to fetch suggestion fields",
          "error"
        );
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch suggestion fields");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchSuggestions = async (
    branchId,
    fieldName,
    searchValue,
    priorityUpperBound
  ) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Fetch suggestion fields";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
      });
      const params =
        "?bid=" +
        branchId +
        "&fieldName=" +
        fieldName +
        "&searchValue=" +
        searchValue +
        "&priorityUpperBound=" +
        priorityUpperBound;
      const url = this.str_URL_BASE + "suggest" + params;
      const response = await window.fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to fetch suggestions for field: " +
            fieldName +
            " with search value: " +
            searchValue,
          "error"
        );
        return [];
      }
    } catch (error) {
      console.error(
        "Failed to fetch suggestions for field: " +
          fieldName +
          " with search value: " +
          searchValue
      );
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchRelatedEntities = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Fetch related entities";
      HttpUtils.addRequest(requestId, requestDescription);
      const headers = new Headers({
        "x-sdd-correlation-id": requestId,
        "x-sdd-correlation-description": requestDescription,
        "content-type": "application/json",
      });
      const url = this.str_URL_BASE + "lookup/dynamicdeletion";

      const response = await window.fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(params),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        response.text().then((error) => {
          console.log(error);
          toastService.func_notify(JSON.parse(error).message, "error");
        });
      }
    } catch (error) {
      console.error("Failed to fetch related entities");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @computed
  get get_str_URL_BASE() {
    return this.str_URL_BASE;
  }

  @action fetchSearchResults = async (props) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Get search results";
      HttpUtils.addRequest(requestId, requestDescription);

      let url = this.str_URL_BASE + "search";
      if (props?.usedForAutocompletion) {
        url += "/autocomplete";
      }
      const headers = new Headers({
        "Content-Type": "application/json",
      });
      const response = await window.fetch(url, {
        method: "POST",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(props),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to get search results. Try different search.",
          "error"
        );
        return {};
      }
    } catch (error) {
      console.error("Failed to get search results");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action fetchExactMatchSearchLookup = async (props) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Get members for exact variable match.";
      HttpUtils.addRequest(requestId, requestDescription);

      const url = this.str_URL_BASE + "navigation/lookup";
      const headers = new Headers({
        "Content-Type": "application/json",
      });
      const response = await window.fetch(url, {
        method: "POST",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(props),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to get members for variable exact match. Try different search.",
          "error"
        );
        return {};
      }
    } catch (error) {
      console.error("Failed to get members for variable exact match");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action fetchEntityDetails = async (props, dontShowSpinningWheel) => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Get entity details";
      if (!dontShowSpinningWheel) {
        HttpUtils.addRequest(requestId, requestDescription);
      }

      const url = this.str_URL_BASE + "view";
      const headers = new Headers({
        "Content-Type": "application/json",
      });
      const response = await window.fetch(url, {
        method: "POST",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
        body: JSON.stringify(props),
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to get entity details",
          "error"
        );
        return {};
      }
    } catch (error) {
      console.error("Failed to get entity details");
    } finally {
      if (!dontShowSpinningWheel) {
        HttpUtils.removeRequest(requestId);
      }
    }
  };

  @action fetchMetadata = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Get metadata";
      HttpUtils.addRequest(requestId, requestDescription);

      const url = this.str_URL_BASE + "metadata/view";
      const headers = new Headers({
        "Content-Type": "application/json",
      });
      const response = await window.fetch(url, {
        method: "GET",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify("Failed to get metadata", "error");
        return {};
      }
    } catch (error) {
      console.error("Failed to get metadata");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action fetchSearchMetadata = async () => {
    const requestId = HttpUtils.generateRequestId();
    try {
      const requestDescription = "Get search metadata";
      HttpUtils.addRequest(requestId, requestDescription);

      const url = this.str_URL_BASE + "metadata/search";
      const headers = new Headers({
        "Content-Type": "application/json",
      });
      const response = await window.fetch(url, {
        method: "GET",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        redirect: "manual",
      });
      HttpUtils.reloadIfRedirectedToLogin(response);

      if (response.status === 200) {
        const json = await response.json();
        return json;
      } else {
        toastService.func_notify(
          "Failed to get search metadata",
          "error"
        );
        return {};
      }
    } catch (error) {
      console.error("Failed to get metadata");
    } finally {
      HttpUtils.removeRequest(requestId);
    }
  };

  @action
  fetchQueryWizardCombinations = async (params, signal) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query QueryWizard combinations";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(
      this.str_URL_BASE + "querywizard/combinations",
      {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        body: JSON.stringify(params),
        signal,
      }
    );
    return response.json();
  };

  @action
  queryMultiSearch = async (params, signal) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query MultiSearch";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(this.str_URL_BASE + "multisearch", {
      method: "POST",
      credentials: "same-origin",
      headers: HttpUtils.amendHeadersIfNeeded(headers),
      body: JSON.stringify(params),
      signal,
    });
    return response.json();
  };

  @action
  transferBirdData = async (params, signal) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Transfering BIRD data";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(this.str_URL_BASE + "bird/transfer", {
      method: "POST",
      credentials: "same-origin",
      headers: HttpUtils.amendHeadersIfNeeded(headers),
      body: JSON.stringify(params),
      signal,
    });
    return response.json();
  };

  @action
  transferBirdContent = async (params, signal) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Transfering BIRD content";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(
      this.str_URL_BASE + "bird/transfercontent",
      {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        body: JSON.stringify(params),
        signal,
      }
    );
    return response.json();
  };

  @action
  getFrameworkChanges = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query framework changes";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(
      this.str_URL_BASE + "changes/framework",
      {
        method: "POST",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
        body: JSON.stringify(params),
      }
    );
    return response.json();
  };

  @action
  getFrameworkReleases = async (frameworkId) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query framework releases";
    HttpUtils.addRequest(requestId, requestDescription);
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(
      this.str_URL_BASE + "framework-releases?frameworkId=" + frameworkId,
      {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
      }
    );
    HttpUtils.removeRequest(requestId);
    return response.json();
  };

  @action
  getPossibleObjectsForComparison = async (objectType, frameworkId, referenceObjectTid, limit = 10, offset = 0, searchExpr) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query Possible Objects for Comparison";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    
    let requestUrl = this.str_URL_BASE + `deep-changes/objects/${objectType}?limit=${limit}&offset=${offset}`;

    if (referenceObjectTid) {
      requestUrl += "&referenceObjectTid=" + referenceObjectTid
    }
    if (frameworkId) {
      requestUrl += "&frameworkId=" + frameworkId
    }
    if (searchExpr) {
      requestUrl += "&search=" + encodeURIComponent(searchExpr)
    }
    const response = await window.fetch(
      requestUrl,
      {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
      }
    );
    return response.json();
  };

  @action
  getAllObjectVersions = async (objectType, objectCode) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query Possible Objects for Comparison";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(
      this.str_URL_BASE +
        "deep-changes/object/versions?type=" +
        objectType +
        "&objectCode=" +
        encodeURIComponent(objectCode),
      {
        method: "GET",
        credentials: "same-origin",
        headers: HttpUtils.amendHeadersIfNeeded(headers),
      }
    );
    return response.json();
  };

  @action
  getDeepChanges = async (params) => {
    const requestId = HttpUtils.generateRequestId();
    const requestDescription = "Query Possible Objects for Comparison";
    const headers = new Headers({
      "Content-Type": "application/json",
      "x-sdd-correlation-id": requestId,
      "x-sdd-correlation-description": requestDescription,
    });
    const response = await window.fetch(this.str_URL_BASE + "deep-changes", {
      method: "POST",
      credentials: "same-origin",
      headers: HttpUtils.amendHeadersIfNeeded(headers),
      body: JSON.stringify(params),
    });
    return response.json();
  };
}
